// Adds an Authorization bearer token, retries once on 401 after refreshing the
// session, and otherwise leaves ofetch's semantics alone.
// Nuxt auto-imports defineNuxtPlugin and composables

/** Whether a rejected $fetch failed with 401. */
function isUnauthorized(error: unknown): boolean {
  const e = error as { statusCode?: number, status?: number, response?: { status?: number } } | null
  return e?.statusCode === 401 || e?.status === 401 || e?.response?.status === 401
}

export default defineNuxtPlugin((nuxtApp) => {
  // Only run on the client — server-side rendering has no Firebase user and
  // replacing globalThis.$fetch there would break Nitro's internal fetching.
  if (!import.meta.client) return

  const { getAuthToken, refreshSession } = useAuthSession()

  const base = $fetch.create({
    async onRequest({ options }) {
      // `options.headers` may already be a Headers instance, in which case
      // assigning `.Authorization` as a plain property is silently ignored —
      // normalise first and set through the Headers API.
      const headers = new Headers(options.headers)
      if (!headers.has('Authorization')) {
        const token = await getAuthToken(false)
        if (token) headers.set('Authorization', `Bearer ${token}`)
      }
      options.headers = headers
    },
  })

  /**
   * Retry once on 401 with a refreshed token.
   *
   * Deliberately *not* ofetch's own `retry`/`retryStatusCodes`: those are
   * per-instance, and payload methods default to zero retries precisely so a
   * non-idempotent POST is never replayed. Enabling retries globally to catch
   * 401s would also replay POSTs on 5xx — a checkout could be submitted twice.
   * Handling it here keeps the retry to exactly one, only on 401.
   *
   * The previous implementation tried to do this inside `onResponseError` by
   * stashing a flag on `ctx.request.context`. `ctx.request` is the URL, so on
   * a string request that threw `TypeError: Cannot create property 'context'
   * on string` — meaning every 401 surfaced as a TypeError instead of being
   * retried. It also returned the retried response from the hook, which ofetch
   * discards.
   */
  function withAuthRetry<T extends (request: never, options?: never) => Promise<unknown>>(call: T): T {
    return (async (request: never, options?: never) => {
      try {
        return await call(request, options)
      }
      catch (error) {
        if (!isUnauthorized(error)) throw error
        if (!(await refreshSession())) throw error

        // Drop the stale header so onRequest attaches the refreshed token.
        const headers = new Headers((options as { headers?: HeadersInit } | undefined)?.headers)
        headers.delete('Authorization')
        return await call(request, { ...(options ?? {}), headers } as never)
      }
    }) as T
  }

  const wrapped = withAuthRetry(base) as typeof $fetch
  wrapped.raw = withAuthRetry(base.raw)
  wrapped.create = base.create

  // Expose on the Nuxt app for explicit consumers.
  nuxtApp.$fetch = wrapped

  // Replace the global $fetch used by auto-imports and composables.
  // Without this, call sites doing `$fetch('/api/...')` bypass the auth
  // interceptor entirely (previously: Bearer header never attached, 401s
  // never retried — which is how an expired session cookie became a hard
  // 401 in the Stripe checkout flow).
  ;(globalThis as unknown as { $fetch: typeof $fetch }).$fetch = wrapped
})
